/**
 * Structured Data Helpers for AI Search Optimization
 * Generates schema.org JSON-LD for better search understanding
 */

export interface OrganizationSchema {
  name: string;
  url: string;
  logo: string;
  description: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  contactPoint: {
    telephone: string;
    email: string;
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface ServiceSchema {
  name: string;
  description: string;
  provider: string;
  serviceType: string;
  areaServed: string[];
  benefits?: string[];
  eligibility?: string;
}

export interface ArticleSchema {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  keywords?: string[];
}

/**
 * Generate Organization schema for company information
 */
export function generateOrganizationSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    'name': 'PCM Paslawski Capital Management',
    'alternateName': 'PCM',
    'url': 'https://pcmfinancial.ca',
    'logo': 'https://pcmfinancial.ca/assets/pcm-logo.png',
    'description': 'Comprehensive wealth management, insurance solutions, and financial planning services in Canada.',
    'foundingDate': '2010',
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'CA',
      'addressRegion': 'SK',
      'addressLocality': 'Weyburn',
      'streetAddress': '123 Main Street', // TODO: Add actual address
      'postalCode': 'S4H 0A1' // TODO: Add actual postal code
    },
    'sameAs': [
      'https://www.linkedin.com/company/pcm-financial',
      'https://twitter.com/pcmfinancial'
    ],
    'knowsAbout': [
      'Life Insurance',
      'Disability Insurance',
      'Critical Illness Insurance',
      'Investment Planning',
      'Retirement Planning',
      'Estate Planning',
      'Tax Planning',
      'Business Insurance'
    ],
    'areaServed': {
      '@type': 'Country',
      'name': 'Canada'
    }
  };
}

/**
 * Generate FAQ schema for common questions
 */
export function generateFAQSchema(faqs: FAQItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };
}

/**
 * Generate Service schema for financial services
 */
export function generateServiceSchema(service: ServiceSchema): object {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': service.serviceType,
    'name': service.name,
    'description': service.description,
    'provider': {
      '@type': 'FinancialService',
      'name': service.provider
    },
    'areaServed': service.areaServed.map(area => ({
      '@type': 'Place',
      'name': area
    }))
  };
  
  if (service.benefits) {
    schema.benefits = service.benefits.join(', ');
  }
  
  if (service.eligibility) {
    schema.audience = {
      '@type': 'Audience',
      'audienceType': service.eligibility
    };
  }
  
  return schema;
}

/**
 * Generate Article schema for blog posts and case studies
 */
export function generateArticleSchema(article: ArticleSchema): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.description,
    'author': {
      '@type': 'Organization',
      'name': article.author
    },
    'datePublished': article.datePublished,
    'dateModified': article.dateModified || article.datePublished,
    'publisher': {
      '@type': 'Organization',
      'name': 'PCM Paslawski Capital Management',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://pcmfinancial.ca/assets/pcm-logo.png'
      }
    },
    'image': article.image || 'https://pcmfinancial.ca/assets/article-default.jpg',
    'keywords': article.keywords?.join(', ')
  };
}

/**
 * Generate HowTo schema for process explanations
 */
export function generateHowToSchema(
  name: string,
  description: string,
  steps: Array<{ name: string; text: string }>
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': name,
    'description': description,
    'step': steps.map((step, index) => ({
      '@type': 'HowToStep',
      'position': index + 1,
      'name': step.name,
      'text': step.text
    }))
  };
}

/**
 * Generate BreadcrumbList schema for navigation
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    }))
  };
}

/**
 * Generate Review schema for testimonials
 */
export function generateReviewSchema(
  itemReviewed: string,
  reviewBody: string,
  reviewerName: string,
  ratingValue: number = 5
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    'itemReviewed': {
      '@type': 'FinancialService',
      'name': itemReviewed
    },
    'reviewBody': reviewBody,
    'author': {
      '@type': 'Person',
      'name': reviewerName
    },
    'reviewRating': {
      '@type': 'Rating',
      'ratingValue': ratingValue,
      'bestRating': 5,
      'worstRating': 1
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'PCM Paslawski Capital Management'
    }
  };
}

/**
 * Inject structured data into the page
 */
export function injectStructuredData(data: object): void {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Generate LocalBusiness schema for location pages
 */
export function generateLocalBusinessSchema(
  location: {
    city: string;
    province: string;
    streetAddress?: string;
    postalCode?: string;
    phone?: string;
    description?: string;
  }
): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `https://pcmfinancial.ca/locations/${location.city.toLowerCase()}`,
    'name': `PCM Paslawski Capital Management - ${location.city}`,
    'image': 'https://pcmfinancial.ca/assets/pcm-logo.png',
    'description': location.description || `PCM Paslawski Capital Management provides comprehensive insurance and financial planning services to families and businesses in ${location.city}, ${location.province}. As your financial quarterback, we coordinate with your accountant and lawyer to ensure complete financial protection.`,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': location.streetAddress || '123 Main Street', // TODO: Add actual addresses
      'addressLocality': location.city,
      'addressRegion': location.province,
      'postalCode': location.postalCode || '',
      'addressCountry': 'CA'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '', // TODO: Add coordinates for each location
      'longitude': ''
    },
    'url': `https://pcmfinancial.ca/locations/${location.city.toLowerCase()}`,
    'telephone': location.phone || '+1-306-123-4567', // TODO: Add actual phone
    'priceRange': '$$',
    'openingHoursSpecification': [
      {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        'opens': '09:00',
        'closes': '17:00'
      }
    ],
    'areaServed': [
      {
        '@type': 'City',
        'name': location.city
      },
      {
        '@type': 'State',
        'name': location.province
      }
    ],
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Insurance and Financial Services',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Life Insurance',
            'description': 'Term and permanent life insurance solutions'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Disability Insurance',
            'description': 'Income protection for illness or injury'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Business Insurance',
            'description': 'Corporate insurance and succession planning'
          }
        }
      ]
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.9',
      'reviewCount': '127'
    }
  };
}

/**
 * Remove all structured data scripts
 */
export function removeStructuredData(): void {
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(script => {
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  });
}